//@ts-nocheck
import * as Sentry from '@sentry/react'
import { createRoot } from 'react-dom/client'
import { testAuthToken, devP2PAppType, environment, secondaryTestAuthTokenObject } from './env/environment.ts'
import { getAuthHeaders, getSecondaryTokenBridgeFunction, setPaddings } from './utils/sessionStorage.ts'
import App from './App.tsx'
import './index.css'
import {
  isDevEnvironment,
  isProdEnvironment,
  logESignEvent,
  markWebviewBoot,
  ESignLogMessage,
  ESignLogLevel,
  initializeESignFlowTracking
} from './utils/utils.ts'
import { initFaro, registerFaroRouter } from './telemetry/initFaro'
import { FallBackPage } from './FallBackPage.tsx'
import { trackEvent } from './utils/appBridge.ts'
import { notifyInstalledUpiIntentsFromFlutter } from './utils/installedUpiIntents.ts'
import { WEBVIEW_RESUME } from './utils/customEvents.ts'
import { seedDevMockInstalledUpiIntentsOnBoot } from './utils/dev/installedUpiIntentsDev.ts'
import { EventNames } from './types/misc.types.ts'
import { NavigationBarType } from './types/misc.types.ts'
import { navigationBarType } from './env/environment.ts'
import { WavePageLoader } from './components/ui-components/loading/WavePageLoader.tsx'
import { ensureConsoleCaptureInstalled } from './components/debug-tool/utils/consoleCapture'

// FaroRoutes needs the router component even when the collector stays off in dev
registerFaroRouter()

// Initialize Sentry only once v2
if (!isDevEnvironment()) {
  Sentry.init({
    dsn: isProdEnvironment()
      ? 'https://bf5b0a958bf928237b426a01c71298b7@o4509161838018560.ingest.us.sentry.io/4509331594412032'
      : 'https://126b227876cd75fd876de073ef14b8c9@o4509161838018560.ingest.us.sentry.io/4509331603980288',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.01,
    tracePropagationTargets: [/^https:\/\/yourserver\.io\/api/],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0
  })
  initFaro()
}

// Critical: Initialize ESign flow tracking and log application start
initializeESignFlowTracking()
logESignEvent(ESignLogMessage.APPLICATION_STARTED)


async function enableMocking() {
  if (import.meta.env.VITE_DISABLE_MSW === '1') {
    return
  }
  if (!isDevEnvironment()) {
    return
  }

  // Dynamic import keeps MSW handlers and mock data out of the production main bundle — do not import them statically
  const { default: handlers } = await import('./mocks/handlers')
  if (handlers?.length > 0) {
    const { worker } = await import('./mocks/browser')

    // `worker.start()` returns a Promise that resolves
    // once the Service Worker is up and ready to intercept requests.
    return worker.start({
      onUnhandledRequest: 'bypass'
    })
  }
}

// Constants
const AUTH_CHECK_INTERVAL = 50
const AUTH_TIMEOUT = 10000
// Users without secondary accounts may never get a token callback — don't block render beyond this
const SECONDARY_AUTH_TIMEOUT = 1500
const DUMMY_USER_DELAY = 1000

// Types
type AuthStatus = 'SUCCESS' | 'ERROR' | 'TIMEOUT'

// Utility functions
const checkAuthData = (): boolean => {
  try {
    const authHeaders = getAuthHeaders()
    const { authorization, 'os-type': osType, 'app-version': appVersion } = authHeaders || {}
    return !!(authorization && osType && appVersion)
  } catch {
    return false
  }
}

const setDummyUser = (): void => {
  const headers = getAuthHeaders()
  const authTokenFromLocalStorage = localStorage.getItem('authToken') || null
  const passFromLocalStorage = localStorage.getItem('password') || null
  let authFromLocalStorage = null
  if (authTokenFromLocalStorage && passFromLocalStorage === 'pu') {
    authFromLocalStorage = authTokenFromLocalStorage
  }
  const authorization = headers?.authorization ?? authFromLocalStorage ?? testAuthToken

  const authHeaders = {
    authorization,
    'os-type': 'IOS',
    'os-version': '17',
    'app-version': '82',
    'app-type': devP2PAppType || '',
    'fingerprint-id': 'abc-123'
  }

  sessionStorage.setItem('authToken', authorization)
  sessionStorage.setItem('auth-header', JSON.stringify(authHeaders))
  const isThreeButton = navigationBarType === NavigationBarType.THREE_BUTTON
  setPaddings({ bottomPadding: isThreeButton ? 36 : 16, topPadding: 44 })
}

const dumpSessionStorage = (): Record<string, string> => {
  const dump: Record<string, string> = {}
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key) {
      dump[key] = sessionStorage.getItem(key) ?? ''
    }
  }
  return dump
}

const parseSecondaryTokensPayload = (tokens: string | Record<string, string>): Record<string, string> => {
  if (typeof tokens !== 'string') {
    return tokens ?? {}
  }

  const trimmed = tokens.trim()
  if (!trimmed) return {}

  try {
    const parsed = JSON.parse(trimmed)
    // Double-encoded: JSON.parse returns a string that is itself JSON
    if (typeof parsed === 'string') {
      return JSON.parse(parsed)
    }
    return parsed ?? {}
  } catch {
    // App may inject escaped JSON, e.g. {\"key\":\"value\"}
    const unescaped = trimmed.replace(/\\"/g, '"')
    const parsed = JSON.parse(unescaped)
    if (typeof parsed === 'string') {
      return JSON.parse(parsed)
    }
    return parsed ?? {}
  }
}

const setDummyAllSecondaryTokens = (): void => {
  const secondaryAuthHeaderFromLocalStorage = localStorage.getItem('secondary-auth-header') || null
  const passFromLocalStorage = localStorage.getItem('password') || null
  if (secondaryAuthHeaderFromLocalStorage && passFromLocalStorage === 'pu') {
    sessionStorage.setItem('secondary-auth-header', secondaryAuthHeaderFromLocalStorage)
    return
  }

  let tokenMap: Record<string, string> = {
    'portfolio-access-token': 'consolidated-portfolio-token',
    'override-portfolio-view-token': 'PORTFOLIO_BASIC_ACCESS'
  }

  if (secondaryTestAuthTokenObject) {
    try {
      tokenMap = parseSecondaryTokensPayload(secondaryTestAuthTokenObject)
    } catch {
      // keep defaults when env JSON is invalid
    }
  }

  sessionStorage.setItem('secondary-auth-header', JSON.stringify(tokenMap))
  console.log('[secondary-auth] setDummyAllSecondaryTokens', {
    tokenMap,
    rawSessionValue: sessionStorage.getItem('secondary-auth-header'),
    sessionStorageDump: dumpSessionStorage()
  })
}

const setupAppBridge = (): void => {
  window.setAutoFillOTP = function (autoFillOTP) {
    window.autoFillOTP = autoFillOTP
    sessionStorage.setItem('autoFillOTP', autoFillOTP)
  }

  window.onWebViewResume = function () {
    window.dispatchEvent(new Event(WEBVIEW_RESUME))
  }

  window.setUniToken = (authToken: string) => {
    window.authToken = authToken
    sessionStorage.setItem('authToken', authToken)
  }

  // DEPRECATED: Use setAllSecondaryTokens instead
  window.setSecondaryToken = (secondaryAuthHeaderKey: string, secondaryAuthHeaderValue: string) => {
    window.secondaryAuthHeaderKey = secondaryAuthHeaderKey
    window.secondaryAuthHeaderValue = secondaryAuthHeaderValue
    sessionStorage?.setItem(
      'secondary-auth-header',
      JSON.stringify({ [secondaryAuthHeaderKey]: secondaryAuthHeaderValue })
    )
    console.log('[secondary-auth] setSecondaryToken', {
      key: secondaryAuthHeaderKey,
      value: secondaryAuthHeaderValue,
      rawSessionValue: sessionStorage?.getItem('secondary-auth-header'),
      authHeader: sessionStorage?.getItem('auth-header')
    })
  }

  window.setAllSecondaryTokens = (tokens: string | Record<string, string>) => {
    console.log('[secondary-auth] setAllSecondaryTokens raw input', {
      type: typeof tokens,
      tokens
    })
    try {
      const tokenMap = parseSecondaryTokensPayload(tokens)
      sessionStorage?.setItem('secondary-auth-header', JSON.stringify(tokenMap ?? {}))
      console.log('[secondary-auth] setAllSecondaryTokens stored', {
        tokenMap,
        rawSessionValue: sessionStorage?.getItem('secondary-auth-header'),
        authHeader: sessionStorage?.getItem('auth-header'),
        sessionStorageDump: dumpSessionStorage()
      })
    } catch (error) {
      console.error('[secondary-auth] Failed to parse secondary tokens:', error, { tokens })
    }
  }

  window.setSafeAreaPadding = (values: string) => {
    try {
      const { bottom, top } = JSON.parse(values)
      setPaddings({ bottomPadding: bottom, topPadding: top })
    } catch (error) {
      console.error('Failed to parse safe area padding:', error)
    }
  }

  window.setUniMetaData = (values: string) => {
    try {
      const { osType, appVersion, osVersion, appType, isDebuggable, fingerprintJsId } = JSON.parse(values)

      const authHeaders = {
        authorization: window.authToken,
        'os-type': osType,
        'os-version': osVersion,
        'app-version': appVersion,
        'app-type': appType,
        'fingerprint-id': fingerprintJsId
      }

      sessionStorage.setItem('auth-header', JSON.stringify(authHeaders))
      // Secondary token bridge depends on app-version; request after metadata is stored.
      requestSecondaryToken()
    } catch (error) {
      console.error('Failed to parse metadata:', error)
    }
  }

  window.setInstalledUpiIntents = (installedUpiApps: string) => {
    localStorage.setItem('installedUpiApps', installedUpiApps)
    notifyInstalledUpiIntentsFromFlutter(installedUpiApps)
  }
}

const requestSecondaryToken = (): void => {
  const message = `{"function":"${getSecondaryTokenBridgeFunction()}"}`
  try {
    if (uniAppHandle) {
      uniAppHandle.postMessage(message)
    } else if (window?.flutter_inappwebview && typeof window?.flutter_inappwebview?.callHandler === 'function') {
      window?.flutter_inappwebview?.callHandler('puHandle', message)
    }
  } catch (error) {
    console.warn('Failed to request secondary token:', error)
  }
}

const requestAppData = (): void => {
  try {
    if (uniAppHandle) {
      uniAppHandle.postMessage('{"function":"token"}')
      uniAppHandle.postMessage('{"function":"metaData"}')
      uniAppHandle.postMessage('{"function":"passSafeAreaPadding"}')
    } else if (window?.flutter_inappwebview && typeof window?.flutter_inappwebview?.callHandler === 'function') {
      window?.flutter_inappwebview?.callHandler('puHandle', '{"function":"token"}')
      window?.flutter_inappwebview?.callHandler('puHandle', '{"function":"metaData"}')
      window?.flutter_inappwebview?.callHandler('puHandle', '{"function":"passSafeAreaPadding"}')
    }
  } catch (error) {
    console.warn('Failed to request app data, will set dummy user:', error)
    setTimeout(setDummyUser, DUMMY_USER_DELAY)
    setTimeout(setDummyAllSecondaryTokens, DUMMY_USER_DELAY)
  }
}

// Polls `check` until it passes or `timeoutMs` elapses
const waitFor = (check: () => boolean, timeoutMs: number): Promise<AuthStatus> => {
  return new Promise((resolve) => {
    if (check()) {
      resolve('SUCCESS')
      return
    }

    let intervalId: NodeJS.Timeout
    let timeoutId: NodeJS.Timeout

    const cleanup = () => {
      if (intervalId) clearInterval(intervalId)
      if (timeoutId) clearTimeout(timeoutId)
    }

    intervalId = setInterval(() => {
      if (check()) {
        cleanup()
        resolve('SUCCESS')
      }
    }, AUTH_CHECK_INTERVAL)

    timeoutId = setTimeout(() => {
      cleanup()
      resolve('TIMEOUT')
    }, timeoutMs)
  })
}

const waitForAuth = (): Promise<AuthStatus> => waitFor(checkAuthData, AUTH_TIMEOUT)

// Session storage is cleared on boot, so any value means the app answered the secondary token request this session
const waitForSecondaryAuth = (): Promise<AuthStatus> =>
  waitFor(() => sessionStorage.getItem('secondary-auth-header') !== null, SECONDARY_AUTH_TIMEOUT)

const renderApp = (root: any, component: React.ReactElement): void => {
  try {
    root.render(component)
  } catch (error) {
    console.error('Failed to render component:', error)
    markWebviewBoot('failed', { message: 'webview_failed_during_init', step: 'render', error })
    root.render(<FallBackPage />)
  }
}

// Main initialization
const initializeApp = async (): Promise<void> => {
  try {
    // Critical: Log app initialization start
    logESignEvent(ESignLogMessage.APP_INITIALIZATION_STARTED)

    // Clear session storage
    sessionStorage.clear()

    // Queue console logs before debug-tool visibility is known (discarded if not enabled)
    ensureConsoleCaptureInstalled()

    // Setup app bridge functions
    setupAppBridge()
    seedDevMockInstalledUpiIntentsOnBoot()

    // Request app data
    requestAppData()

    const root = createRoot(document.getElementById('root')!)
    renderApp(root, <WavePageLoader />)
    // index.html reads this at 20s visible. Still `loader` then means the auth wait never moved on.
    markWebviewBoot('loader')

    // Wait for authentication
    const authStatus = await waitForAuth()

    // Critical: Log auth result
    logESignEvent(ESignLogMessage.AUTHENTICATION_COMPLETED, ESignLogLevel.INFO, {
      authStatus
    })

    // Secondary tokens are requested only after metadata lands; wait so first API calls carry them
    // (queries use staleTime: Infinity, so a token-less first response would stick for the session)
    if (authStatus === 'TIMEOUT') {
      markWebviewBoot('auth-timeout')
    } else if (authStatus === 'SUCCESS') {
      markWebviewBoot('auth-ok', { step: 'token' })
      const secondaryAuthStatus = await waitForSecondaryAuth()
      console.log('[secondary-auth] waitForSecondaryAuth', { secondaryAuthStatus })
      markWebviewBoot('auth-ok', { step: 'secondary_token' })
    }

    // Handle auth result
    // if (authStatus === 'TIMEOUT') {
    // logESignEvent(ESignLogMessage.AUTH_TIMEOUT_FALLBACK, ESignLogLevel.ERROR)
    //   renderApp(root, <FallBackPage />)
    //   return
    // }

    // Critical: Log before rendering main app
    logESignEvent(ESignLogMessage.APP_COMPONENT_MOUNTED)

    // Render main app
    renderApp(
      root,
      <Sentry.ErrorBoundary
        beforeCapture={(_scope, error) => {
          markWebviewBoot('failed', { message: 'webview_failed_in_app', error })
        }}
        fallback={<FallBackPage />}
      >
        <App />
      </Sentry.ErrorBoundary>
    )
  } catch (error) {
    console.error('App initialization failed:', error)
    logESignEvent(ESignLogMessage.APP_INITIALIZATION_FAILED, ESignLogLevel.ERROR, {
      error: error?.message
    })
    markWebviewBoot('failed', { message: 'webview_failed_during_init', step: 'init', error })
    const root = createRoot(document.getElementById('root')!)
    renderApp(root, <FallBackPage />)
  }
}

if (window?.location?.pathname?.includes('esign')) {
  trackEvent({
    eventName: EventNames.ScreenLoaded,
    payload: {
      eventId: 'requested_esign',
      screenType: 'MF_ESign_APP'
    }
  })
}

// Start the app
enableMocking()
  .then(initializeApp)
  .catch((error) => {
    console.error('Failed to enable mocking:', error)
    // Fallback to direct initialization
    initializeApp()
  })
